/**
 * Subscribe, uncomp, end-promotion, change-offer and apply-promotion conflict copy (TMMAPI-0112,
 * 0124, 0125, 0127; TMMRSL-0068, 0070, 0072). `null` for any other error, so the caller falls through to
 * `describeAction`. The server's message is never shown.
 */
import type { TFunction } from 'i18next';
import { backendCode, backendDetail } from '@/api/errors';
import { rateCardErrorMessage } from '@/features/rateCards/rateCardErrors';
import { inviteEmailError } from '@/tenant/inviteGuard';

const SUBSCRIPTION_ERRORS: Readonly<Record<string, string>> = {
  SUBSCRIPTION_IN_PROGRESS: 'subscribers.error.inProgress',
  SUBSCRIPTION_EXISTS: 'subscribers.error.exists',
  // End promotion with nothing applied (TMMAPI-0124; the old `404 PROMOTION_NOT_FOUND` is gone).
  PROMOTION_NOT_APPLIED: 'subscribers.error.noPromotion',
  // Subscribe, change-offer and uncomp, when pay-api answers a plan other than the one asked for
  // (TMMAPI-0125). Nothing was bound or sent, so it is not a setup conflict and nothing refreshes.
  PLAN_MAPPING_CONFLICT: 'subscribers.error.planMapping',
  // The same routes when pay-api refused the plan (TMMAPI-0127, `502`); nothing was bound or sent.
  PLAN_CREATE_FAILED: 'subscribers.error.planCreate',
};

/** The plan codes subscribe, change-offer, uncomp and an `OFFER` apply-promotion share. */
function planErrorMessage(err: unknown, t: TFunction): string | null {
  const code = backendCode(err);
  if (code === 'PLAN_MAPPING_CONFLICT') return t('subscribers.error.planMapping');
  if (code === 'PLAN_CREATE_FAILED') return t('subscribers.error.planCreate');
  return null;
}

export function subscriberErrorMessage(err: unknown, t: TFunction): string | null {
  const invite = inviteEmailError(err, t);
  if (invite !== null) return invite;
  const code = backendCode(err);
  if (code === undefined || !Object.hasOwn(SUBSCRIPTION_ERRORS, code)) return null;
  return t(SUBSCRIPTION_ERRORS[code] as string);
}

/** The two 0112 subscribe/uncomp conflicts that refresh the page and keep the dialog open. */
export function isSubscriptionSetupConflict(err: unknown): boolean {
  const code = backendCode(err);
  return code === 'SUBSCRIPTION_IN_PROGRESS' || code === 'SUBSCRIPTION_EXISTS';
}

/**
 * Change-offer's `409 OFFER_NOT_ACTIVE` (TMMAPI-0119): the picked offer has closed since the list
 * was loaded.
 */
export function isOfferClosed(err: unknown): boolean {
  return backendCode(err) === 'OFFER_NOT_ACTIVE';
}

/** Change-offer's own codes, then the rate-card codes; `null` for any other error. */
export function changeOfferErrorMessage(err: unknown, t: TFunction): string | null {
  if (isOfferClosed(err)) return t('subscribers.error.offerClosed');
  return planErrorMessage(err, t) ?? rateCardErrorMessage(err, t);
}

/**
 * Apply-promotion's conflicts (TMMAPI-0124): `409 PROMOTION_ALREADY_APPLIED`, and
 * `409 PROMOTION_NOT_REDEEMABLE` by its `details.reason`. A reason the console doesn't know (or
 * none) gets the general copy. An `OFFER` code's change-offer can create the offer's plan, so the
 * plan codes apply too (TMMAPI-0125, 0127). `null` for any other error.
 */
export function applyPromotionErrorMessage(err: unknown, t: TFunction): string | null {
  const plan = planErrorMessage(err, t);
  if (plan !== null) return plan;
  const code = backendCode(err);
  if (code === 'PROMOTION_ALREADY_APPLIED') return t('subscribers.error.promoAlreadyApplied');
  if (code !== 'PROMOTION_NOT_REDEEMABLE') return null;
  switch (backendDetail(err, 'reason')) {
    case 'ALREADY_APPLIED':
      return t('subscribers.error.promoAlreadyApplied');
    case 'EXHAUSTED':
      return t('subscribers.error.promoExhausted');
    case 'PAUSED':
      return t('subscribers.error.promoPaused');
    case 'EXPIRED':
      return t('subscribers.error.promoNotValid');
    case 'NEW_SUBSCRIBERS_ONLY':
      return t('subscribers.error.promoNewOnly');
    case 'TOO_LATE':
      return t('subscribers.error.promoTooLate');
    case 'WRONG_CARD':
      return t('subscribers.error.promoOfferUnavailable');
    default:
      return t('subscribers.error.promoNotRedeemable');
  }
}
